import { TenantFeatureMatrix } from '@app/features/TenantFeatureMatrix'
import { tenantTypeSelectData } from '@lib/tenantTypes'
import { useTenant } from '@lib/useTenant'
import { useTenants } from '@lib/tenantsContext'
import { useClickOutside } from '@mantine/hooks'
import { useSupabase } from '@packages/supabase'
import {
  ActionIcon,
  Alert,
  Button,
  Grid,
  Group,
  Popover,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea
} from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import { modals } from '@mantine/modals'
import {
  CircleButton,
  H1,
  H3,
  openConfirmModal,
  SortableTable
} from '@packages/ui'
import { upperCaseFirst } from '@packages/utils'
import {
  IconAlertTriangle,
  IconCalendar,
  IconCancel,
  IconLock,
  IconLockOpen,
  IconCheck,
  IconX,
  IconCircleChevronLeft,
  IconRestore
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { DeleteTenantModal } from './components/DeleteTenantModal'
import { TenantActivitySummary } from './components/TenantActivitySummary'
import { TenantBillingPanel } from './components/TenantBillingPanel'
import { TenantIntegrationsPanel } from './components/TenantIntegrationsPanel'

export const Tenant = () => {
  const state = useLocation().state || {}
  const { supabase } = useSupabase()
  const params = useParams()
  const [subscription, setSubscription] = useState<Subscription>(null)
  const [products, setProducts] = useState<any[]>([])
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [creatingSubscription, setCreatingSubscription] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [tenantType, setTenantType] = useState('')
  const [tenantName, setTenantName] = useState(state.tenant.name)
  const [savingName, setSavingName] = useState(false)
  const [updatingType, setUpdatingType] = useState(false)
  const [assumingTenant, setAssumingTenant] = useState(false)
  const [unassumingTenant, setUnassumingTenant] = useState(false)
  const [roles, setRoles] = useState<Array<{ value: string; label: string }>>(
    []
  )
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRoleId, setInviteRoleId] = useState<string | null>(null)
  const [invitingUser, setInvitingUser] = useState(false)
  const [inviteResultMessage, setInviteResultMessage] = useState<string | null>(
    null
  )
  const navigate = useNavigate()

  // datepicker
  const [showTrialDatePicker, setShowTrialDatePicker] = useState(false)
  const datePickerRef = useClickOutside(() => setShowTrialDatePicker(false))

  const tenant = useTenant(state.tenant.id || params.tenantId, state.tenant)
  const tenants = useTenants()

  const statusText = useMemo(() => {
    if (!tenant.data) return ''
    if (
      tenant.data?.demo_expiry_date &&
      dayjs(tenant.data.demo_expiry_date) < dayjs()
    )
      return 'expired'

    if (tenant.data.suspended_at) {
      return 'suspended'
    }

    return subscription?.status || 'not set'
  }, [subscription, tenant])

  // Fetch products from Supabase RPC
  const fetchProducts = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase.rpc('get_stripe_products')
      setLoading(false)
      if (error) {
        console.error('Error fetching products:', error)
        return []
      }
      setProducts(data || [])
      return data || []
    } catch (err: any) {
      console.error('Error fetching products:', err)
      setLoading(false)
      return []
    }
  }, [])

  // Fetch subscription for a tenants from Supabase Edge Function
  const fetchSubscription = useCallback(async (tenantId: string) => {
    setLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke(
        'stripe/subscription',
        {
          method: 'GET',
          body: { tenantId }
        }
      )
      setLoading(false)
      if (error) {
        console.error('Error fetching subscription:', error)
        return null
      }
      return data?.subscription || null
    } catch (err: any) {
      console.error('Error fetching subscription:', err)
      setLoading(false)
      return null
    }
  }, [])

  // Create stripe customer for a tenants
  const createCustomer = useCallback(
    async (tenantId: string, email: string, name: string) => {
      setLoading(true)
      try {
        const { data, error } = await supabase.functions.invoke(
          'stripe/create-customer',
          {
            method: 'POST',
            body: { tenantId, email, name }
          }
        )
        setLoading(false)
        if (error) {
          console.error('Error creating customer:', error)
          return null
        }
        return data?.customerId || null
      } catch (err: any) {
        console.error('Error creating customer:', err)
        setLoading(false)
        return null
      }
    },
    []
  )

  // Create stripe subscription for a tenants
  const createSubscription = useCallback(
    async (tenantId: string, customerId: string, priceId: string) => {
      setLoading(true)
      try {
        const { data, error } = await supabase.functions.invoke(
          'stripe/create-subscription',
          {
            method: 'POST',
            body: { tenantId, customerId, priceId }
          }
        )
        setLoading(false)
        if (error) {
          console.error('Error creating subscription:', error)
          return null
        }
        return data?.subscriptionId || null
      } catch (err: any) {
        console.error('Error creating subscription:', err)
        setLoading(false)
        return null
      }
    },
    []
  )

  const handleCreateCustomer = async () => {
    setCreatingCustomer(true)
    setError(null)
    try {
      const customerId = await createCustomer(
        tenant.data.id,
        tenant.data.email || tenant.data.contact_email,
        tenant.data.tenant_name || tenant.data.name
      )
      if (customerId) {
        await tenant.update({ stripe_customer_id: customerId })
      }
    } catch (err: any) {
      setError(err.message)
    }
    setCreatingCustomer(false)
  }

  const handleCreateSubscriptionWithPrice = async (priceId: string) => {
    if (!priceId || !tenant.data?.stripe_customer_id) return
    setCreatingSubscription(true)
    setError(null)
    try {
      const subscriptionId = await createSubscription(
        tenant.data.id,
        tenant.data.stripe_customer_id,
        priceId
      )
      if (subscriptionId) {
        await tenant.update({ stripe_subscription_id: subscriptionId })
        fetchSubscription(tenant.data.id).then(setSubscription)
        await tenant.mutate()
      }
    } catch (err: any) {
      setError(err.message)
    }
    setCreatingSubscription(false)
  }

  const handleCreateSubscription = async () => {
    if (!selectedPlan) return
    await handleCreateSubscriptionWithPrice(selectedPlan)
  }

  const handleSetTrialDate = (date: Date | string | null) => {
    if (!date) {
      return
    }
    // Handle both Date objects and string dates
    const dateObj = date instanceof Date ? date : new Date(date)
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date:', date)
      return
    }
    tenant.update({ demo_expiry_date: dateObj.toISOString() })
  }

  const handleSaveTenantName = async () => {
    if (!tenant.data?.id) return
    setSavingName(true)
    setError(null)
    try {
      await tenant.update({ name: tenantName } as any)
      await tenant.mutate()
    } catch (err: any) {
      setError(err?.message || 'Failed to update tenant name')
    } finally {
      setSavingName(false)
    }
  }

  const handleSaveTenantType = async () => {
    if (!tenant.data?.id) return
    setUpdatingType(true)
    setError(null)
    try {
      await tenant.update({ type: tenantType } as any)
      await tenant.mutate()
    } catch (err: any) {
      setError(err?.message || 'Failed to update tenant type')
    } finally {
      setUpdatingType(false)
    }
  }

  const loadTenantRoles = useCallback(
    async (tenantId: string) => {
      const { data, error } = await supabase.rpc('admin_fetch_tenant_roles', {
        p_tenant_id: tenantId
      })

      if (error) {
        console.error('Error loading roles:', error)
        return
      }

      const roleOptions = (data || []).map((role: any) => ({
        value: role.id,
        label: role.name
      }))
      setRoles(roleOptions)

      if (roleOptions.length > 0 && !inviteRoleId) {
        const adminRole =
          roleOptions.find((r) => r.label.toLowerCase() === 'admin') ||
          roleOptions[0]
        setInviteRoleId(adminRole.value)
      }
    },
    [inviteRoleId, supabase]
  )

  const handleAssumeTenant = async () => {
    if (!tenant.data?.id) return
    setAssumingTenant(true)
    setError(null)
    setInviteResultMessage(null)
    try {
      const { error: rpcError } = await supabase.rpc(
        'admin_attach_self_to_tenant',
        {
          p_tenant_id: tenant.data.id
        }
      )
      if (rpcError) {
        setError(rpcError.message)
        return
      }
      await supabase.auth.refreshSession()
      setInviteResultMessage(
        'Attached to tenant and refreshed JWT claims. You can now access this tenant context in the web app.'
      )
    } catch (err: any) {
      setError(err?.message || 'Failed to assume tenant')
    } finally {
      setAssumingTenant(false)
    }
  }

  const handleInviteUserToTenant = async () => {
    if (!tenant.data?.id || !inviteEmail.trim() || !inviteRoleId) return
    setInvitingUser(true)
    setError(null)
    setInviteResultMessage(null)
    try {
      const returnUrl = `${import.meta.env.VITE_HOST}/invite`
      const { data: inviteRows, error: rpcError } = await supabase.rpc(
        'admin_create_tenant_invite',
        {
          p_tenant_id: tenant.data.id,
          p_email: inviteEmail.trim(),
          p_full_name: inviteName.trim() || inviteEmail.trim(),
          p_role_id: inviteRoleId,
          p_user_type: 'worker',
          p_language: 'en',
          p_return_url: returnUrl
        }
      )

      if (rpcError) {
        setError(rpcError.message)
        return
      }

      const createdInvite = Array.isArray(inviteRows) ? inviteRows[0] : null
      if (!createdInvite?.invite_id) {
        setError('Failed to create invite')
        return
      }

      const { data: mailData, error: mailError } =
        await supabase.functions.invoke('admin_send_invite_email', {
          body: {
            inviteId: createdInvite.invite_id,
            returnUrl
          }
        })

      if (mailError) {
        setError(mailError.message)
        return
      }

      if (!mailData?.success) {
        setError(mailData?.message || 'Invite created but email sending failed')
        return
      }

      setInviteResultMessage(`Invite sent to ${inviteEmail.trim()}`)
      setInviteEmail('')
      setInviteName('')
    } catch (err: any) {
      setError(err?.message || 'Failed to invite user')
    } finally {
      setInvitingUser(false)
    }
  }

  const handleUnassumeTenant = async () => {
    setUnassumingTenant(true)
    setError(null)
    setInviteResultMessage(null)
    try {
      const { data, error: rpcError } = await supabase.rpc(
        'admin_unassume_self_tenant'
      )
      if (rpcError) {
        setError(rpcError.message)
        return
      }

      await supabase.auth.refreshSession()
      const switchedTenantId = Array.isArray(data) ? data[0]?.tenant_id : null
      setInviteResultMessage(
        switchedTenantId
          ? `Switched back to tenant ${switchedTenantId}.`
          : 'Switched back to another active tenant.'
      )
    } catch (err: any) {
      setError(err?.message || 'Failed to unassume tenant')
    } finally {
      setUnassumingTenant(false)
    }
  }

  useEffect(() => {
    if (!tenant.data?.id) return
    setTenantType((tenant.data as any)?.type || '')
    setTenantName((tenant.data as any)?.name || '')
    loadTenantRoles(tenant.data.id)
    fetchProducts().then((data) => {
      if (data) setProducts(data)
    })
    fetchSubscription(tenant.data.id).then((data) => {
      if (data) setSubscription(data)
    })
  }, [tenant.data?.id, fetchProducts, fetchSubscription, loadTenantRoles])

  const handleRemoveTrial = () => {
    openConfirmModal({
      title: 'Remove Trial Period ',
      children: (
        <Text c={'black'}>
          Remove trial date? This tenant wont have a time limitation on their
          account anymore.
        </Text>
      ),
      onConfirm: () => {
        tenant.update({ demo_expiry_date: null })
      }
    })
  }

  const BlockTenantModal = ({
    onConfirm
  }: {
    onConfirm: (note: string) => void
    onCancel: () => void
  }) => {
    const [note, setNote] = useState('')

    return (
      <Stack>
        <Text c={'black'}>
          Are you sure you want to block this tenant? All users except the owner
          will be unable to log in.
        </Text>
        <Textarea
          label="Reason (optional)"
          placeholder="Enter reason for blocking..."
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Group justify="center">
          <CircleButton
            onClick={() => onConfirm(note.trim() || '')}
            color="red"
            icon={<IconCheck />}
          >
            Block Tenant
          </CircleButton>
        </Group>
      </Stack>
    )
  }

  const handleBlockTenant = () => {
    modals.open({
      title: 'Block Tenant',
      children: (
        <BlockTenantModal
          onConfirm={async (note) => {
            try {
              const result = await tenant.update({
                suspended_at: new Date().toISOString(),
                suspended_note: note || null
              })
              if (!result) {
                setError('Failed to block tenants. Please try again.')
              } else {
                setError(null)
                modals.closeAll()
              }
            } catch (err: any) {
              setError(err.message || 'Failed to block tenants')
            }
          }}
          onCancel={() => modals.closeAll()}
        />
      )
    })
  }

  const UnblockTenantModal = ({
    onConfirm
  }: {
    onConfirm: () => void
    onCancel: () => void
  }) => {
    return (
      <Stack>
        <Text c={'black'}>
          Are you sure you want to unblock this tenant? All users will be able
          to log in again.
        </Text>
        <Group justify="center">
          <CircleButton onClick={onConfirm} color="green" icon={<IconCheck />}>
            Unblock Tenant
          </CircleButton>
        </Group>
      </Stack>
    )
  }

  const handleRestoreTenant = async () => {
    if (!tenant.data?.id) return
    setError(null)
    try {
      const { error: rpcErr } = await supabase.rpc(
        'admin_cancel_tenant_deletion',
        {
          p_tenant_id: tenant.data.id,
          p_reason: 'Restored via admin UI'
        }
      )
      if (rpcErr) {
        setError(rpcErr.message)
        return
      }
      await tenant.mutate()
    } catch (err: any) {
      setError(err?.message ?? 'Failed to restore tenant')
    }
  }

  const handleUnblockTenant = () => {
    modals.open({
      title: 'Unblock Tenant',
      children: (
        <UnblockTenantModal
          onConfirm={async () => {
            try {
              const result = await tenant.update({
                suspended_at: null,
                suspended_note: null
              })
              if (!result) {
                setError('Failed to unblock tenants. Please try again.')
              } else {
                setError(null)
                modals.closeAll()
              }
            } catch (err: any) {
              setError(err.message || 'Failed to unblock tenants')
            }
          }}
          onCancel={() => modals.closeAll()}
        />
      )
    })
  }

  if (!tenant.data.id) {
    return (
      <Stack c={'white'}>
        <H1>Loading tenant...</H1>
      </Stack>
    )
  }

  return (
    <Stack>
      <Stack className={'text-white'}>
        <Group justify={'space-between'}>
          <H1>
            <ActionIcon
              variant={'transparent'}
              color={'white'}
              size={'xl'}
              onClick={() => navigate('/tenants')}
            >
              <IconCircleChevronLeft size={40} />
            </ActionIcon>
            {tenant.data?.name}{' '}
            {tenant.data.demo_expiry_date &&
              dayjs(tenant.data.demo_expiry_date).isBefore(new Date()) && (
                <div
                  className={
                    'inline-flex rounded-2xl border bg-white/30 p-2 text-sm'
                  }
                >
                  Trial expired on{' '}
                  {dayjs(tenant.data.demo_expiry_date).format('DD.MM.YYYY')}
                </div>
              )}
            {tenant.data.suspended_at && (
              <div
                className={
                  'inline-flex rounded-2xl border bg-red-500/30 p-2 text-sm text-white'
                }
              >
                <IconLock size={16} className="mr-1" />
                Suspended since{' '}
                {dayjs(tenant.data.suspended_at).format('DD.MM.YYYY HH:mm')}
              </div>
            )}
          </H1>

          <Button
            c={'white'}
            leftSection={<IconX />}
            variant={'outline'}
            onClick={() => navigate('/tenants')}
          >
            Close
          </Button>
        </Group>

        {tenant.data?.deletion_requested_at && (
          <Alert
            color="red"
            icon={<IconAlertTriangle />}
            variant="filled"
            title="Pending deletion"
          >
            <Stack gap={6}>
              <Text size="sm">
                Requested by{' '}
                <strong>
                  {(tenant.data as any)?.deletion_requested_by ??
                    'unknown user'}
                </strong>{' '}
                on{' '}
                {dayjs(
                  (tenant.data as any).deletion_requested_at as string
                ).format('DD.MM.YYYY HH:mm')}
                .
                {(tenant.data as any)?.deletion_grace_until && (
                  <>
                    {' '}
                    Scheduled permanent purge:{' '}
                    <strong>
                      {dayjs(
                        (tenant.data as any).deletion_grace_until as string
                      ).format('DD.MM.YYYY HH:mm')}
                    </strong>{' '}
                    (
                    {Math.max(
                      0,
                      dayjs(
                        (tenant.data as any).deletion_grace_until as string
                      ).diff(dayjs(), 'day')
                    )}{' '}
                    days left).
                  </>
                )}
              </Text>
              {(tenant.data as any)?.deletion_reason && (
                <Text size="sm">
                  Reason:{' '}
                  <em>{(tenant.data as any).deletion_reason as string}</em>
                </Text>
              )}
              {(tenant.data as any)?.deletion_in_progress_at ? (
                <Text size="sm" fw={600}>
                  Hard purge already in progress &mdash; cannot be cancelled.
                </Text>
              ) : (
                <Group gap="xs" mt={4}>
                  <Button
                    size="xs"
                    color="white"
                    variant="white"
                    leftSection={<IconRestore size={14} />}
                    onClick={handleRestoreTenant}
                  >
                    Restore tenant
                  </Button>
                </Group>
              )}
            </Stack>
          </Alert>
        )}

        <Stack className={'rounded bg-white/10 p-3 text-white'}>
          {error && <Text c="red">{error}</Text>}

          <Tabs defaultValue="general" color="cyan">
            <Tabs.List>
              <Tabs.Tab value="general">Overview</Tabs.Tab>
              <Tabs.Tab value="features">Features</Tabs.Tab>
              <Tabs.Tab value="api">API keys</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="general" pt="md">
              <Stack gap="md">
                {tenant.data?.id && (
                  <TenantActivitySummary tenantId={tenant.data.id} />
                )}

                <H3>Tenant Details</H3>
                <TextInput
                  label="Tenant Name"
                  placeholder=""
                  value={tenantName}
                  rightSection={
                    <Group className={'text-black'}>
                      {tenantName !== tenant.data.name && (
                        <ActionIcon
                          loading={savingName}
                          variant={'transparent'}
                          className={'text-black'}
                          onClick={handleSaveTenantName}
                        >
                          <IconCheck />
                        </ActionIcon>
                      )}
                    </Group>
                  }
                  onChange={(e) => setTenantName(e.currentTarget.value)}
                />

                <H3>Status</H3>
                <Grid w={'100%'} className={'text-sm'}>
                  <Grid.Col span={3}>Type:</Grid.Col>
                  <Grid.Col
                    span={9}
                    className={'align-center flex flex-row space-x-2'}
                  >
                    <Group>
                      <Select
                        value={tenantType}
                        data={tenantTypeSelectData()}
                        onChange={(value) => setTenantType(value || '')}
                        w={220}
                      />
                      <Button
                        size="xs"
                        variant="default"
                        loading={updatingType}
                        onClick={handleSaveTenantType}
                      >
                        Save Type
                      </Button>
                    </Group>
                  </Grid.Col>

                  <Grid.Col span={3}>Trial:</Grid.Col>
                  <Grid.Col
                    span={9}
                    className={'align-center flex flex-row space-x-2'}
                  >
                    <Group>
                      <div className={'align-middle text-sm'}>
                        {tenant.data.demo_expiry_date
                          ? dayjs(tenant.data.demo_expiry_date).format(
                              'DD.MM.YYYY'
                            )
                          : '-'}
                      </div>
                      <Button
                        size={'xs'}
                        variant={'default'}
                        leftSection={<IconCancel />}
                        onClick={handleRemoveTrial}
                      >
                        Remove trial
                      </Button>

                      <Popover opened={showTrialDatePicker}>
                        <Popover.Target>
                          <Button
                            size={'xs'}
                            variant={'default'}
                            leftSection={<IconCalendar />}
                            onClick={() => setShowTrialDatePicker(true)}
                          >
                            Set Trial End Date
                          </Button>
                        </Popover.Target>

                        <Popover.Dropdown ref={datePickerRef}>
                          <DatePicker
                            value={
                              tenant.data.demo_expiry_date
                                ? new Date(tenant.data.demo_expiry_date)
                                : null
                            }
                            onChange={(date) => {
                              handleSetTrialDate(date)
                              setShowTrialDatePicker(false)
                            }}
                          />
                        </Popover.Dropdown>
                      </Popover>
                    </Group>
                  </Grid.Col>

                  <Grid.Col span={3}>Status:</Grid.Col>
                  <Grid.Col
                    span={9}
                    className={'align-center flex flex-row space-x-2'}
                  >
                    <Group>
                      {upperCaseFirst(statusText)}

                      {tenant.data.suspended_at ? (
                        <Button
                          size={'xs'}
                          variant={'default'}
                          color="green"
                          leftSection={<IconLockOpen size={16} />}
                          onClick={handleUnblockTenant}
                        >
                          Unblock Tenant
                        </Button>
                      ) : (
                        <Button
                          size={'xs'}
                          variant={'default'}
                          color="red"
                          leftSection={<IconLock size={16} />}
                          onClick={handleBlockTenant}
                        >
                          Block Tenant
                        </Button>
                      )}
                    </Group>
                  </Grid.Col>

                  {tenant.data.suspended_at && (
                    <>
                      <Grid.Col span={3}>Suspended Since:</Grid.Col>
                      <Grid.Col span={9}>
                        {dayjs(tenant.data.suspended_at).format(
                          'DD.MM.YYYY HH:mm'
                        )}
                      </Grid.Col>
                      {tenant.data.suspended_note && (
                        <>
                          <Grid.Col span={3}>Suspension Reason:</Grid.Col>
                          <Grid.Col span={9}>
                            {tenant.data.suspended_note}
                          </Grid.Col>
                        </>
                      )}
                    </>
                  )}
                </Grid>

                <H3>Owner Contact</H3>
                <SortableTable
                  data={[
                    {
                      name: 'NO NAME YET'
                    }
                  ]}
                  columns={[{ label: 'name', key: 'name' }]}
                ></SortableTable>

                <TenantBillingPanel
                  tenant={tenant.data}
                  subscription={subscription}
                  stripeLoading={loading}
                  creatingCustomer={creatingCustomer}
                  creatingSubscription={creatingSubscription}
                  onReload={async () => {
                    await tenant.mutate()
                    if (tenant.data?.id) {
                      const sub = await fetchSubscription(tenant.data.id)
                      setSubscription(sub)
                    }
                  }}
                  onCreateCustomer={() => handleCreateCustomer()}
                  onCreateSubscription={(priceId) => {
                    setSelectedPlan(priceId)
                    return handleCreateSubscriptionWithPrice(priceId)
                  }}
                />

                <Stack>
                  <H3>Invite user to this tenant</H3>
                  <Stack>
                    <Group grow>
                      <TextInput
                        label="Email"
                        placeholder="name@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.currentTarget.value)}
                      />
                      <TextInput
                        label="Full name"
                        placeholder="Jane Doe"
                        value={inviteName}
                        onChange={(e) => setInviteName(e.currentTarget.value)}
                      />
                    </Group>
                    <Group>
                      <Select
                        label="Role"
                        placeholder="Select role"
                        data={roles}
                        value={inviteRoleId}
                        onChange={setInviteRoleId}
                        w={320}
                      />
                      <Button
                        mt={24}
                        loading={invitingUser}
                        onClick={handleInviteUserToTenant}
                        disabled={!inviteEmail.trim() || !inviteRoleId}
                      >
                        Send invite
                      </Button>
                    </Group>
                    {inviteResultMessage && (
                      <Text c="green">{inviteResultMessage}</Text>
                    )}
                  </Stack>
                </Stack>

                <Stack>
                  <H3>House keeping</H3>
                  <Group>
                    <Button
                      variant="outline"
                      loading={assumingTenant}
                      onClick={handleAssumeTenant}
                    >
                      Assume Tenant Access
                    </Button>
                    <Button
                      variant="outline"
                      loading={unassumingTenant}
                      onClick={handleUnassumeTenant}
                    >
                      Unassume / Switch Back
                    </Button>
                    <Button
                      variant={'alert'}
                      color="red"
                      ml={'auto'}
                      disabled={
                        !!tenant.data?.is_master_tenant ||
                        !!tenant.data?.deletion_in_progress_at
                      }
                      onClick={() => {
                        // Tracks whether the orchestrator successfully finished so
                        // that when the user manually dismisses the report panel we
                        // navigate away from the (now-deleted) tenant detail page.
                        let didDelete = false
                        modals.open({
                          title: 'Delete Tenant',
                          size: 'xl',
                          // Prevent accidental dismissal so the user can read the
                          // per-step deletion report before closing.
                          closeOnClickOutside: false,
                          closeOnEscape: false,
                          children: (
                            <DeleteTenantModal
                              tenant={tenant.data as any}
                              callbacks={{
                                fetchPreview: async (tenantId) => {
                                  const { data, error } = await supabase.rpc(
                                    'admin_get_tenant_deletion_preview',
                                    { p_tenant_id: tenantId }
                                  )
                                  if (error) throw new Error(error.message)
                                  return data as any
                                },
                                confirmDeletion: async ({
                                  tenantId,
                                  typedName,
                                  reason
                                }) => {
                                  const { data: reqData, error: reqErr } =
                                    await supabase.rpc(
                                      'admin_request_tenant_deletion',
                                      {
                                        p_tenant_id: tenantId,
                                        p_typed_name: typedName,
                                        p_reason: reason
                                      }
                                    )
                                  if (reqErr)
                                    throw new Error(
                                      `request: ${reqErr.message}`
                                    )
                                  const token = (
                                    reqData as { confirmation_token?: string }
                                  )?.confirmation_token
                                  if (!token)
                                    throw new Error(
                                      'No confirmation token returned'
                                    )

                                  const { data: res, error: invokeErr } =
                                    await supabase.functions.invoke(
                                      'admin-delete-tenant',
                                      {
                                        body: {
                                          tenant_id: tenantId,
                                          confirmation_token: token,
                                          typed_name: typedName,
                                          reason: reason
                                        }
                                      }
                                    )
                                  if (invokeErr)
                                    throw new Error(
                                      `deletion: ${invokeErr.message}`
                                    )
                                  return res as any
                                }
                              }}
                              onClose={() => {
                                modals.closeAll()
                                if (didDelete) navigate('/tenants')
                              }}
                              onDeleted={() => {
                                didDelete = true
                                // Refresh the admin tenants list so the just-deleted
                                // tenant disappears immediately when we navigate back.
                                void tenants.mutate()
                              }}
                            />
                          )
                        })
                      }}
                    >
                      Delete Tenant
                    </Button>
                  </Group>
                </Stack>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="api" pt="md">
              {tenant.data?.id && <TenantIntegrationsPanel />}
            </Tabs.Panel>

            <Tabs.Panel value="features" pt="md">
              {tenant.data?.id && (
                <TenantFeatureMatrix
                  tenantId={tenant.data.id}
                  tenantType={(tenant.data as { type?: string }).type ?? ''}
                  billingPlan={
                    (tenant.data as { billing_plan?: string | null })
                      .billing_plan
                  }
                />
              )}
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Stack>
    </Stack>
  )
}
